// DS: C25L
// @dynamic-i18n-keys
//   who.empty.var.comment
//   who.empty.var.favourite
import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import classnames from 'classnames';
import { UserRelationType } from './redux/types';
import { PapiDeviation } from '@wix/da-papi-types';
import UserAvatar from '../User/Avatar';
import UserLink from '../User/Link';
import UserName from '../User/BasicUserName';
import StreamedList from '../StreamedList';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import {
  BiContextProvidedValue,
  BiData,
  DefaultBiEvent,
} from '@wix/da-bi/pkg/events';
import BiButton from '@wix/da-bi/pkg/Components/BiButton';
import { useStream } from '../utils/hooks/useStream';
import { showFavouritedByModal } from '../Modals/redux/actionCreators';
import { initializeUserStream } from './redux/actions';

import s from './Who.scss';

function getStreamIdForType(type: UserRelationType, deviationId: number) {
  return `@@who:${deviationId}:${type}`;
}

export interface Props {
  deviation: PapiDeviation;
  userListType: UserRelationType;
}

const biSectionMap: { [k in UserRelationType]: string } = {
  comment: 'comments_pop_up',
  favourite: 'fave_pop_up',
};

const NUM_USERS_SHOWN = 4;

const Who: React.FC<React.PropsWithChildren<Props>> = ({
  userListType,
  deviation,
  deviation: { deviationId },
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isOpen, setOpen] = useState(false);

  const streamId = useMemo(
    () => getStreamIdForType(userListType, deviationId),
    [userListType, deviationId]
  );

  const {
    items: users,
    total,
    isFetching,
    isInitialized,
  } = useStream(streamId);

  // Initialize stream if needed
  useEffect(() => {
    if (!isInitialized) {
      dispatch(initializeUserStream(streamId, deviationId, userListType));
    }
  }, [streamId, deviationId, userListType, isInitialized, dispatch]);

  const totalToShow = total || users.length;
  const hasOverflow = totalToShow > NUM_USERS_SHOWN;
  const isEmpty = totalToShow === 0;
  const isFetchingInitial = isFetching && users.length === 0;
  const visibleSlice =
    !hasOverflow || isOpen ? users : users.slice(0, NUM_USERS_SHOWN);
  const remainingCount =
    hasOverflow && !isOpen ? totalToShow - visibleSlice.length : 0;

  const userList = visibleSlice.map(user => (
    <UserLink user={user} className={s['user']} key={user.userId}>
      <span className={s['user-avatar']}>
        <UserAvatar size={16} user={user} withLink={false} />
      </span>
      <span className={s['username']}>
        <UserName user={user} />
      </span>
    </UserLink>
  ));

  const onClickMore = () => {
    if (userListType === 'favourite') {
      dispatch(showFavouritedByModal(deviation));
    } else {
      setOpen(true);
    }
  };

  return (
    <BiLoggerContextProvider
      value={{ sectionname: biSectionMap[userListType] }}
    >
      <div
        className={classnames(
          'no-theme-skin',
          s['root'],
          hasOverflow && s['overflow'],
          isOpen && s['open'],
          (isEmpty || !isInitialized || isFetchingInitial) && s['empty']
        )}
      >
        {isFetchingInitial || !isInitialized ? (
          <div>{t('who.fetching')}</div>
        ) : isEmpty ? (
          <div>{t(`who.empty.var.${userListType}`)}</div>
        ) : isOpen ? (
          <StreamedList
            className={s['list']}
            wrapperClassName={s['scrollable']}
            bottomedClassName={s['bottomed']}
            scrolledClassName={s['scrolled']}
            streamId={streamId}
          >
            {userList}
          </StreamedList>
        ) : (
          <>
            <div className={s['list']}>{userList}</div>
            {hasOverflow && (
              <BiButton
                className={s['more']}
                onClick={onClickMore}
                biData={BiData<DefaultBiEvent>({
                  evid: 2,
                  click_info: 'deviation.who.more',
                  sectionname: BiContextProvidedValue,
                  widgetname: BiContextProvidedValue,
                  typeid: BiContextProvidedValue,
                  itemid: BiContextProvidedValue,
                })}
              >
                {t('who.more', { count: remainingCount })}
              </BiButton>
            )}
          </>
        )}
      </div>
    </BiLoggerContextProvider>
  );
};
Who.displayName = 'Who';

export default Who;
